import React, { useCallback, useState } from "react";
import { Button, Modal, Steps, notification } from "antd";
import { useTranslation } from "react-i18next";

import { useBulkCreateCsvTransactionsMutation } from "../../../../../api/graphql/mutations/transaction/bulkCreateCsvTransactions";
import { BankFormat, ColumnMapping } from "./bankFormats";
import { CsvParseResult, ParsedRow, applyMappings } from "./parseCsv";
import UploadStep from "./steps/UploadStep";
import MappingStep from "./steps/MappingStep";
import PreviewStep, { PreviewRow } from "./steps/PreviewStep";

interface Props {
  email: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 0 | 1 | 2;

const CsvImportModal = ({ email, open, onClose, onSuccess }: Props) => {
  const { t } = useTranslation("CsvImport");

  const [step, setStep] = useState<Step>(0);

  // Step 1 state
  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);

  // Step 2 state
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [detectedFormat, setDetectedFormat] = useState<BankFormat | null>(null);
  const [rawRows, setRawRows] = useState<ParsedRow[]>([]);

  // Step 3 state
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);

  const [bulkCreate, { loading: submitting }] =
    useBulkCreateCsvTransactionsMutation();

  const reset = useCallback(() => {
    setStep(0);
    setParseResult(null);
    setMappings([]);
    setDetectedFormat(null);
    setRawRows([]);
    setPreviewRows([]);
    setSelectedKeys([]);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // ── Step 1 → 2 ────────────────────────────────────────────────────────────

  const handleParsed = useCallback((result: CsvParseResult) => {
    setParseResult(result);
    setMappings(result.mappings);
    setDetectedFormat(result.detectedFormat);
    setRawRows(result.rows);
    setStep(1);
  }, []);

  // ── Step 2 → 3 ────────────────────────────────────────────────────────────

  const handleToPreview = useCallback(() => {
    const rows = applyMappings(
      rawRows,
      mappings,
      detectedFormat
    ) as PreviewRow[];
    setPreviewRows(rows);
    // Default: all valid rows selected
    setSelectedKeys(rows.filter((r) => !r._hasError).map((r) => r._rowIndex));
    setStep(2);
  }, [rawRows, mappings, detectedFormat]);

  // ── Step 3: Confirm ───────────────────────────────────────────────────────

  const handleConfirm = useCallback(async () => {
    const toCreate = previewRows.filter(
      (r) => !r._hasError && selectedKeys.includes(r._rowIndex)
    );

    if (toCreate.length === 0) {
      notification.warning({ message: t("notifNoTransactions") });
      return;
    }

    try {
      await bulkCreate({
        variables: {
          email,
          transactions: toCreate.map(
            ({ amount, paymentDate, currency, name, iban, description }) => ({
              amount,
              paymentDate,
              currency,
              name,
              iban,
              description,
            })
          ),
        },
      });

      notification.success({
        message: t("notifSuccess", { count: toCreate.length }),
      });
      reset();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      notification.error({ message: t("notifError", { message }) });
    }
  }, [
    previewRows,
    selectedKeys,
    bulkCreate,
    email,
    reset,
    onSuccess,
    onClose,
    t,
  ]);

  // ── Navigation helpers ────────────────────────────────────────────────────

  const canAdvanceFromMapping =
    mappings.some((m) => m.targetField === "amount") &&
    mappings.some((m) => m.targetField === "paymentDate");

  const stepTitles = [t("stepUpload"), t("stepMapColumns"), t("stepPreview")];

  const footer = (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <Button
        onClick={step === 0 ? handleClose : () => setStep((step - 1) as Step)}
      >
        {step === 0 ? t("btnCancel") : t("btnBack")}
      </Button>
      <div>
        {step === 1 && (
          <Button
            type="primary"
            disabled={!canAdvanceFromMapping}
            onClick={handleToPreview}
          >
            {t("btnPreview")}
          </Button>
        )}
        {step === 2 && (
          <Button
            type="primary"
            loading={submitting}
            disabled={selectedKeys.length === 0}
            onClick={handleConfirm}
          >
            {t("btnImport", { count: selectedKeys.length })}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      title={t("modalTitle")}
      open={open}
      onCancel={handleClose}
      footer={footer}
      width={step === 2 ? 900 : 700}
      destroyOnClose
    >
      <Steps
        current={step}
        items={stepTitles.map((title) => ({ title }))}
        style={{ marginBottom: 24 }}
        size="small"
      />

      {step === 0 && <UploadStep onParsed={handleParsed} />}

      {step === 1 && parseResult && (
        <MappingStep
          mappings={mappings}
          detectedFormat={detectedFormat}
          previewRows={parseResult.rows.slice(0, 3).map((r) => r.raw)}
          onChange={setMappings}
        />
      )}

      {step === 2 && (
        <PreviewStep
          rows={previewRows}
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
        />
      )}
    </Modal>
  );
};

export default CsvImportModal;
